import { useEffect, useMemo } from 'react';
import { useBasket } from '~/features/basket';
import useProducts from '~/features/commerce-api/hooks/useProducts';
import { DropPointDto } from '~/lib';
import { ShippingMethod } from '~/lib/data-contract/salesforce';
import { useFrame } from '~/shared/utils/frame/hooks/useFrame';
import { splitByDeliveryType } from '../utils/splitByDeliveryType';
import { useCheckoutState } from './useCheckoutState';
import { useDroppoints } from './useDroppoints';
import useShipmentMethods from './useShipmentMethods';
import { useErrorHandler } from '~/shared/utils/errorBoundary/hooks/useErrorHandler';

export const useCheckout = () => {
    const { trackAndDisplayError: trackError } = useErrorHandler();

    const basket = useBasket();
    const checkoutStore = useCheckoutState();

    const {
        carrier,
        billingAddress,
        zipCode,
        shipmentId,
        setCarrier: setCarrrierInClient,
        deliveryMethod,
        isUsingAlternateAddress,
        setDeliveryAddress,
        setIsUsingAlternateAddress,
        setCurrentShipmentStep,
        alternateShippingAddress,
        setVendorSelectedDroppoint,
        setChangingCarrier,
    } = checkoutStore;

    const { getProductsByShipment } = useProducts();

    const {
        data: { marketSelector },
    } = useFrame();

    const { data: shipmentMethods, refetch: refetchShipmentMethods } = useShipmentMethods({
        id: shipmentId,
    });

    const carriers = useMemo(
        () => splitByDeliveryType(shipmentMethods as ShippingMethod[]),
        [shipmentMethods],
    );

    const shipmentType = useMemo(
        () =>
            basket?.shipments?.find((shipment) => shipment.shipmentId === shipmentId)
                ?.c_shipmentType,
        [shipmentId, shipmentMethods],
    );

    const shipmentMethod = useMemo(
        () =>
            basket?.shipments?.find((shipment) => shipment.shipmentId === shipmentId)
                ?.shippingMethod,
        [shipmentId, shipmentMethods],
    );

    const freightGroup = useMemo(() => {
        const shipment = basket?.shipments?.find((shipment) => shipment.shipmentId === shipmentId);
        return shipment?.shippingMethod?.c_freightGroup;
    }, [shipmentId]);

    const vendorSelectedDroppointHandler = useMemo(
        () =>
            freightGroup ? ['A', 'B'].includes(freightGroup) && shipmentType === 'external' : false,
        [freightGroup, shipmentId],
    );

    const currentShipment = useMemo(() => {
        return basket?.shipments?.find((shipment) => shipment.shipmentId === shipmentId);
    }, [shipmentMethods]);

    const setUsingAlternateDeliveryAddress = (usingAlternate: boolean) =>
        setIsUsingAlternateAddress(usingAlternate);

    const resetCurrentShipmentStep = () => {
        setCurrentShipmentStep(1);
    };

    const {
        data: mapMarkers,
        // error: dropPointError,
        isFetching: dropPointIsFetching,
    } = useDroppoints({
        carrier: {
            id: Number(carrier && deliveryMethod && carrier[deliveryMethod]?.c_webshipperId),
            serviceCode:
                carrier && deliveryMethod && carrier[deliveryMethod]?.c_webshipperServiceCode,
        },
        address: {
            street: zipCode === billingAddress?.postalCode ? billingAddress?.address1 : '',
            city: zipCode === billingAddress?.postalCode ? billingAddress?.city : '',
            zip: zipCode === billingAddress?.postalCode ? billingAddress?.postalCode : zipCode,
            countryCode: marketSelector?.markets?.[0].label?.toUpperCase(),
        },
        shipmentId,
        select: (res) => {
            return res.map((point: DropPointDto) => ({
                ...point,
                _id: `${point.latitude}${point.longitude}`,
            })) as DropPointDto[];
        },
        disabled: vendorSelectedDroppointHandler,
        zipCode,
    });

    const setCarrier = async (carrier?: ShippingMethod) => {
        setCarrrierInClient(carrier);
        if (carrier) {
            setChangingCarrier(true);
            try {
                await basket.setShippingMethod(carrier.id, shipmentId);
                setChangingCarrier(false);
            } catch (e) {
                trackError(e);
            }
        }
    };

    const setShippingAddressInformation = () => {
        if (isUsingAlternateAddress) {
            setDeliveryAddress(alternateShippingAddress);
            // make sure to reset condition
            setIsUsingAlternateAddress(false);
        } else {
            setDeliveryAddress({
                firstName: billingAddress?.firstName || '',
                lastName: billingAddress?.lastName || '',
                address1: billingAddress?.address1 || '',
                city: billingAddress?.city || '',
                postalCode: billingAddress?.postalCode || '',
                phone: billingAddress?.phoneMobile,
                //@ts-ignore
                deliveryType: 'alternative',
            });
        }
    };

    useEffect(() => {
        setVendorSelectedDroppoint(vendorSelectedDroppointHandler);
    }, [vendorSelectedDroppointHandler]);

    return {
        ...checkoutStore,
        carriers,
        refetchShipmentMethods,
        getProductsByShipment,
        mapMarkers,
        setUsingAlternateDeliveryAddress,
        shipmentType,
        freightGroup,
        shipmentMethods,
        shipmentMethod,
        currentShipment,
        resetCurrentShipmentStep,
        setShippingAddressInformation,
        setCarrier,
        dropPointIsFetching,
    };
};
